import React, { useEffect } from "react";
import styled from "styled-components";
import Breadcrumbs from "../../components/UI/Breadcrumbs/Breadcrumbs.tsx";
import Feed from "../../components/Dashboard/Feed/Feed";
import AllProjectsSkeleton from "../../components/Dashboard/AllProjects/AllProjectsSkeleton/AllProjectsSkeleton";
import { connect } from "react-redux";
import style from "./MarketplaceExplorar.module.scss";
import {
  clearOnboardingForms,
  clearProjects,
  closeMainModal,
  openFeedTextModal,
} from "../../store/actions";
import BotoneraModern from "../../components/Botonera/BotoneraModern.tsx";
import NewNavImproved from "../../components/Nav/NewNavImproved.tsx";
import useNavVisibility from "../../hooks/useNavVisibility.ts";
import usePremiumCheck from "../../hooks/usePremiumCheck";
import { fireAnalytics } from "../../utils/firebase";
import { Plus } from "lucide-react";
import { getSessionId, getDeviceCategory, getPreviousPage, getDashboardSource } from "../../utils/analyticsHelpers.ts";

const Container = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  padding-right: 5%;
  padding-left: 5%;
  max-width: 150rem;
  margin: auto;

  @media (max-width: 1200px) {
    padding-right: 5%;
    padding-left: 5%;
    }

  @media (max-width: 768px) {
    padding-right: 0;
    padding-left: 0;
  }
`;

interface MarketplaceExplorarProps {
  userData?: any;
  isLoaded: boolean;
  clearProjects: () => void;
  closeMainModal: () => void;
  openFeedTextModal: (config: { text: boolean }) => void;
}

const MarketplaceExplorar: React.FC<MarketplaceExplorarProps> = ({ 
  userData, 
  isLoaded,
  clearProjects,
  closeMainModal,
  openFeedTextModal,
}) => {
  const { ocultarEnMobile, mobileStylesFromParent } = useNavVisibility();
  const premiumCheck = usePremiumCheck();

  useEffect(() => {
    window.scrollTo(0, 0);
    clearOnboardingForms();
    clearProjects();
    
    // Mantener evento antiguo durante transición
    fireAnalytics("marketplace_explorar_view", { 
      action: "view", 
      marketplace_user_viewer_info: userData?.id // Parámetro antiguo
    });
    
    // Nuevo evento estandarizado
    fireAnalytics("page_marketplace_explorar_view", {
      user_id: userData?.id || "",
      session_id: getSessionId(),
      source: getDashboardSource(),
      previous_page: getPreviousPage(),
      device_category: getDeviceCategory(),
      category: "navigation",
      action: "page_view",
      page_name: "marketplace_explorar"
    });

    return () => {
      window.scrollTo(0, 0);
    };
  }, [clearProjects, userData?.id]);

  const handleCreatePublication = () => {
    fireAnalytics("marketplace_crear_publicacion_intent", {
      action: "create_publication_intent",
      marketplace_user_info: userData?.id
    });
    openFeedTextModal({
      text: true,
    });
  };

  return (
    <Container>
      {!ocultarEnMobile && <NewNavImproved />}
      {ocultarEnMobile && <BotoneraModern mobileStylesFromParent={mobileStylesFromParent} />}
      
      <div className={style.mainContainer}>
        <div className={style.headerSection}>
          <Breadcrumbs />
          <div className={style.headerContent}>
            
            <div className={style.headerText}>
              <h1 className={style.title}>
                Explora <span className={style.highlight}>Servicios y Productos</span>
              </h1>
              <p className={style.subtitle}>
                Descubre lo que otros profesionales están ofreciendo en la plataforma
              </p>
            </div>
            <button 
              className={style.createButton}
              onClick={premiumCheck(handleCreatePublication)}
            >
              <Plus size={20} />
              <span>Crear publicación</span>
            </button>
          </div>
        </div>
        
        <div className={style.feedContainer}>
          {isLoaded ? (
            <Feed />
          ) : (
            <AllProjectsSkeleton />
          )}
        </div>
      </div>
    </Container>
  );
}

const mapStateToProps = (state: any) => {
  return {
    isLoaded: state.general.isLoaded,
    userData: state.general.userData,
  };
};

export default connect(mapStateToProps, {
  clearProjects,
  closeMainModal,
  openFeedTextModal,
})(MarketplaceExplorar);
